import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync } from "./utils";

const TAG = {
  green: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]",
  blue: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]",
  orange: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]",
  yellow: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]",
  red: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]",
};

function Tag({ cls = "blue", children }) {
  return <span className={`inline-flex items-center px-[10px] py-[6px] rounded-full text-[11px] font-semibold border ${TAG[cls]}`}>{children}</span>;
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-[#1D3469] mb-[2px]">{title}</div>
          {note && <div className="text-sm text-gray-500">{note}</div>}
        </div>
        {headerRight && <div className="flex flex-wrap gap-[10px]">{headerRight}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function HelpTooltip({ title, desc, example }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <span
        className="w-[18px] h-[18px] rounded-full bg-[#eef6fc] border border-[#cfe3f3] text-[#2d81c1] text-[11px] font-bold inline-flex items-center justify-center cursor-help"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >?</span>
      {show && (
        <div className="absolute left-6 top-0 z-50 w-[260px] bg-white border border-[#e6ecf3] rounded-[14px] p-3 shadow-[0_16px_36px_rgba(19,31,47,.12)] text-[12px]">
          <strong className="text-[#233445] block mb-1">{title}</strong>
          <p className="text-[#617587] mb-2">{desc}</p>
          {example && <div className="bg-[#f5f7fb] rounded-[8px] p-2 text-[#617587] italic">{example}</div>}
        </div>
      )}
    </span>
  );
}

const DEFAULT_DATA = {
  vision: "", mission: "", supportingLine: "", versionType: "Current Active Version",
  values: [], cultureStatement: "", cultureThemes: ["", "", ""],
  ultimateOutcome: "", studentOutcome: "", institutionalOutcome: "",
  revisionHistory: [], remarks: "",
};

function calculateCompletion(data) {
  const fields = [data.vision, data.mission, data.supportingLine, data.versionType];
  const valuesCount = Array.isArray(data.values) && data.values.length > 0 ? 1 : 0;
  const cultureCount = data.cultureStatement ? 1 : 0;
  const max = fields.length + 2;
  const filled = fields.filter(Boolean).length + valuesCount + cultureCount;
  return Math.round((filled / max) * 100);
}

export default function InstitutionalIdentity() {
  const settings = getSchoolSettings();
  const [data, setData] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.identity, null);
    return stored ? { ...DEFAULT_DATA, ...stored, values: stored.values || [], cultureThemes: stored.cultureThemes || ["","",""], revisionHistory: stored.revisionHistory || [] } : { ...DEFAULT_DATA };
  });
  const [valueForm, setValueForm] = useState({ name: "", desc: "" });
  const [historyForm, setHistoryForm] = useState({ date: "", by: "", change: "" });
  const [editingValueIdx, setEditingValueIdx] = useState(null);
  const [editingHistoryIdx, setEditingHistoryIdx] = useState(null);
  const [activeTab, setActiveTab] = useState("identity");
  const [searchQuery, setSearchQuery] = useState("");
  const [valueStatusFilter, setValueStatusFilter] = useState("");
  const [cultureStatusFilter, setCultureStatusFilter] = useState("");
  const [versionQuick, setVersionQuick] = useState(data.versionType || "Current Active Version");

  useEffect(() => {
    loadModuleData("identity", null).then(d => {
      if (d) setData({ ...DEFAULT_DATA, ...d, values: d.values || [], cultureThemes: d.cultureThemes || ["","",""], revisionHistory: d.revisionHistory || [] });
    });
  }, []);

  function persist(updated, label = "Identity saved") {
    saveModuleData("identity", updated, calculateCompletion(updated));
    addActivityAsync("Institutional Identity", label, "Saved", "green");
    setData(updated);
  }

  function handleSaveIdentity() {
    if (!data.vision.trim() || !data.mission.trim()) {
      showToast("Vision and Mission are required.", "error");
      return;
    }
    persist(data, "Vision & Mission saved");
    showToast("Vision & Mission saved.", "success");
  }

  function handleAddValue() {
    if (!valueForm.name.trim()) { showToast("Value name is required.", "error"); return; }
    let values;
    if (editingValueIdx !== null) {
      values = data.values.map((v, i) => i === editingValueIdx ? valueForm : v);
      showToast("Value updated.", "success");
    } else {
      values = [...data.values, valueForm];
      showToast("Value added.", "success");
    }
    const updated = { ...data, values };
    persist(updated, "Value added");
    setValueForm({ name: "", desc: "" });
    setEditingValueIdx(null);
  }

  function handleDeleteValue(i) {
    const updated = { ...data, values: data.values.filter((_, idx) => idx !== i) };
    persist(updated, "Value deleted");
    showToast("Value deleted.", "warn");
  }

  function handleSaveCulture() {
    if (!data.cultureStatement.trim()) { showToast("Culture statement is required.", "error"); return; }
    persist(data, "Culture saved");
    showToast("Culture saved.", "success");
  }

  function handleSaveOutcome() {
    if (!data.ultimateOutcome.trim()) { showToast("Ultimate outcome statement is required.", "error"); return; }
    persist(data, "Ultimate outcome saved");
    showToast("Ultimate outcome saved.", "success");
  }

  function handleAddHistory() {
    if (!historyForm.date || !historyForm.by.trim() || !historyForm.change.trim()) {
      showToast("Date, Edited By, and Change are required.", "error"); return;
    }
    let revisionHistory;
    if (editingHistoryIdx !== null) {
      revisionHistory = data.revisionHistory.map((h, i) => i === editingHistoryIdx ? historyForm : h);
      showToast("Entry updated.", "success");
    } else {
      revisionHistory = [...data.revisionHistory, historyForm];
      showToast("Entry added.", "success");
    }
    const updated = { ...data, revisionHistory };
    persist(updated, "Revision entry added");
    setHistoryForm({ date: "", by: "", change: "" });
    setEditingHistoryIdx(null);
  }

  function handleFillSample() {
    const sample = {
      ...data,
      vision: "To nurture future-ready, values-driven, confident, and capable learners through meaningful education.",
      mission: "We deliver meaningful teaching, build strong systems, support teachers, and engage stakeholders to improve student outcomes consistently across all grades.",
      supportingLine: "We aim to develop students who are academically capable, emotionally balanced, and socially responsible.",
      versionType: "Current Active Version",
      values: [
        { name: "Excellence", desc: "We pursue the highest standard in everything we do." },
        { name: "Respect", desc: "Every individual in our community is treated with dignity." },
        { name: "Integrity", desc: "We act with honesty and transparency at all times." },
        { name: "Growth", desc: "We believe learning is a lifelong journey." },
      ],
      cultureStatement: "Our school is a place of learning, respect, and growth. We are committed to creating a safe, supportive, and high-expectation environment where every student and teacher can thrive.",
      cultureThemes: ["Learning-Centered", "Trust-Based", "Accountability-Driven"],
      ultimateOutcome: "To produce graduates who are confident, ethical, academically strong, and capable of navigating a complex world.",
      studentOutcome: "Future-ready, ethical, resilient learners",
      institutionalOutcome: "Trusted, high-performing, future-focused school",
    };
    persist(sample, "Sample identity data loaded");
    showToast("Sample Identity content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved data in Institutional Identity. Continue?")) return;
    saveModuleData("identity", DEFAULT_DATA, 0);
    addActivityAsync("Institutional Identity", "Module cleared", "Cleared", "red");
    setData({ ...DEFAULT_DATA });
    showToast("Institutional Identity module cleared.", "warn");
  }

  const completion = calculateCompletion(data);
  const cultureThemeCount = data.cultureThemes ? data.cultureThemes.filter(Boolean).length : 0;
  const readiness = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";

  const tabBtn = (tab, label) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-5 py-[10px] rounded-[12px] text-[13px] font-semibold transition-colors ${activeTab === tab ? "bg-[#f47c20] text-white" : "bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#eef6fc] hover:text-[#2d81c1]"}`}
    >
      {label}
    </button>
  );

  const labelClass = "text-[12px] font-semibold text-[#233445] mb-1 flex items-center gap-1";
  const inputClass = "h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] w-full";
  const textareaClass = "border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y w-full";

  return (

<>
      <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Institutional Identity</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 mb-8 leading-[1.4]">
                Define vision, mission, values, culture, and institutional direction before deeper school planning begins.
              </p>
            </div>

            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Module Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "Based on required identity fields and entries", bar: true },
                { label: "Values Added", icon: "💠", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: data.values.length, meta: "Core values currently saved in this module" },
                { label: "Culture Themes", icon: "🏫", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: cultureThemeCount, meta: "Culture focus areas currently added" },
                { label: "Revision Entries", icon: "🕒", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: data.revisionHistory.length, meta: "Saved updates for this identity module" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-[10px] mb-4">
                    <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                    <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>{s.icon}</div>
                  </div>
                  <div>
                    <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                    <div className="text-[12px] text-[#617587]">{s.meta}</div>
                  </div>
                  {s.bar && <div className="w-full h-[6px] rounded-full bg-[#edf2f8] overflow-hidden mt-2"><div className="h-full rounded-full bg-gradient-to-r from-[#f47c20] to-[#2d81c1]" style={{ width: `${completion}%` }} /></div>}
                </div>
              ))}
            </section>

            <div className="flex flex-wrap gap-3 mb-6">
              {[["identity","Vision & Mission"],["values","Values"],["culture","Culture"],["outcome","Ultimate Outcome"],["history","Revision History"],["preview","Live Preview"]].map(([tab, label]) => tabBtn(tab, label))}
            </div>

            {/* Vision & Mission Tab */}
            {activeTab === "identity" && (
              <div className="flex flex-col gap-[18px]">
              <div className="grid gap-[18px]" style={{ gridTemplateColumns: "1.2fr .8fr" }}>
                <Window title="Identity Entry Window" note="Add the school's long-term direction and way of working." headerRight={
                  <>
                    <button onClick={() => setData(d => ({ ...d, vision: "", mission: "", supportingLine: "" }))} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Fields</button>
                    <button onClick={handleSaveIdentity} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">Save Vision & Mission</button>
                  </>
                }>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className={labelClass}>
                        Vision
                        <HelpTooltip title="What belongs here" desc="Vision explains what the school ultimately wants to become or create in the long run." example="Example: To nurture future-ready, values-driven, confident, and capable learners through meaningful education." />
                      </label>
                      <textarea value={data.vision} onChange={e => setData(d => ({ ...d, vision: e.target.value }))} placeholder="Write the school's Vision here..." rows={3} className={textareaClass} />
                      <div className="text-[11px] text-[#8ea0b1] mt-1">Keep it aspirational, simple, and long-term.</div>
                    </div>
                    <div>
                      <label className={labelClass}>
                        Mission
                        <HelpTooltip title="What belongs here" desc="Mission explains how the school will work daily and strategically to achieve the vision." example="Example: We deliver meaningful teaching, build strong systems, support teachers, and engage stakeholders to improve student outcomes." />
                      </label>
                      <textarea value={data.mission} onChange={e => setData(d => ({ ...d, mission: e.target.value }))} placeholder="Write the school's Mission here..." rows={3} className={textareaClass} />
                      <div className="text-[11px] text-[#8ea0b1] mt-1">Mission should explain the practical path, not just ambition.</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>
                          Supporting Line for Vision
                          <HelpTooltip title="How to use this" desc="Add one optional supporting line if the school wants to clarify the vision further." example="Example: We aim to develop students who are academically capable, emotionally balanced, and socially responsible." />
                        </label>
                        <input value={data.supportingLine} onChange={e => setData(d => ({ ...d, supportingLine: e.target.value }))} placeholder="Add a supporting line if required..." className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Version Type</label>
                        <select value={data.versionType} onChange={e => setData(d => ({ ...d, versionType: e.target.value }))} className={inputClass}>
                          {["Current Active Version","Draft Version","Revised Version"].map(v => <option key={v}>{v}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] px-4 py-3 text-[12px] text-[#617587]">
                      {data.vision ? `Vision saved. Mission: ${data.mission ? "saved" : "not added"}.` : "Nothing saved yet in this section."}
                    </div>
                  </div>
                </Window>

                <Window title="Quick Understanding" note="Simple logic behind this module.">
                  {[
                    { title: "Vision and Mission should come first", text: "This module should be completed before moving into deeper planning because all later modules should align with it." },
                    { title: "Vision = Final Direction", text: "Vision explains what the school wants to become or create over time." },
                    { title: "Mission = Way of Working", text: "Mission explains how the school will work daily and strategically to move toward that vision." },
                    { title: "Connected next step", text: "After identity is clear, the school can define values, culture, and then take those into diagnosis and strategic planning." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>

              {data.remarks && (
                <div className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] px-4 py-3">
                  <div className="text-[12px] font-semibold text-[#233445] mb-1">Additional Remarks / Principal Priorities</div>
                  <div className="text-[12px] text-[#617587]">{data.remarks}</div>
                </div>
              )}
              </div>
            )}

            {/* Values Tab */}
            {activeTab === "values" && (
              <Window title="Values" note="Add core values and short meaning lines for each." headerRight={
                <>
                  <button onClick={() => { setValueForm({ name: "", desc: "" }); setEditingValueIdx(null); }} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Input</button>
                  <button onClick={handleAddValue} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">{editingValueIdx !== null ? "Update Value" : "Add Value"}</button>
                </>
              }>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div><label className={labelClass}>Value Name</label><input value={valueForm.name} onChange={e => setValueForm(f => ({ ...f, name: e.target.value }))} placeholder="Enter value name..." className={inputClass} /></div>
                  <div><label className={labelClass}>Short Descriptor</label><input value={valueForm.desc} onChange={e => setValueForm(f => ({ ...f, desc: e.target.value }))} placeholder="Write short descriptor..." className={inputClass} /></div>
                </div>
                {data.values.length > 0 && (
                  <div className="mb-4 max-w-xs">
                    <label className={labelClass}>Filter by Status</label>
                    <select value={valueStatusFilter} onChange={e => setValueStatusFilter(e.target.value)} className={inputClass}>
                      <option value="">All Statuses</option>
                      <option value="Added">Added</option>
                    </select>
                  </div>
                )}
                <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                  <table className="w-full border-collapse">
                    <thead><tr>{["Value","Descriptor","Status","Actions"].map(h => <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>)}</tr></thead>
                    <tbody>
                      {!data.values.length ? <tr><td colSpan={4} className="text-center py-6 text-[13px] text-[#617587]">No values added yet.</td></tr>
                        : (valueStatusFilter && valueStatusFilter !== "Added" ? [] : data.values).length === 0 ? <tr><td colSpan={4} className="text-center py-6 text-[13px] text-[#617587]">No values match the current filter.</td></tr>
                        : (valueStatusFilter && valueStatusFilter !== "Added" ? [] : data.values).map((v, i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] font-semibold text-[#233445] border-b border-[#eef2f7]">{v.name}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#617587] border-b border-[#eef2f7]">{v.desc}</td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><Tag cls="green">Added</Tag></td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><div className="flex gap-2">
                              <button onClick={() => { setValueForm(v); setEditingValueIdx(i); }} className="px-3 py-1 rounded-[8px] bg-[#eef6fc] text-[#2d81c1] text-[11px] font-semibold hover:bg-[#cfe3f3]">Edit</button>
                              <button onClick={() => handleDeleteValue(i)} className="px-3 py-1 rounded-[8px] bg-[#fff0f0] text-[#d95353] text-[11px] font-semibold hover:bg-[#f4c5c5]">Delete</button>
                            </div></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Window>
            )}

            {/* Culture Tab */}
            {activeTab === "culture" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Culture Statement" note="Define how the school should behave and work daily." headerRight={
                  <>
                    <button onClick={() => setData(d => ({ ...d, cultureStatement: "", cultureThemes: ["","",""] }))} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset</button>
                    <button onClick={handleSaveCulture} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">Save Culture</button>
                  </>
                }>
                  <div className="flex flex-col gap-4">
                    <div><label className={labelClass}>Culture Statement</label><textarea value={data.cultureStatement} onChange={e => setData(d => ({ ...d, cultureStatement: e.target.value }))} placeholder="Write the school's culture statement here..." rows={3} className={textareaClass} /></div>
                    {[1,2,3].map(n => (
                      <div key={n}><label className={labelClass}>Culture Theme {n}</label><input value={data.cultureThemes[n-1] || ""} onChange={e => { const t = [...data.cultureThemes]; t[n-1] = e.target.value; setData(d => ({ ...d, cultureThemes: t })); }} placeholder="Enter culture theme..." className={inputClass} /></div>
                    ))}
                  </div>
                </Window>

                <Window title="Culture Snapshot" note="Shows current saved culture direction.">
                  <div className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3">
                    <div className="text-[11px] font-semibold text-[#8ea0b1] uppercase tracking-wider mb-2">Saved Culture Statement</div>
                    <div className="text-[13px] text-[#233445]">{data.cultureStatement || "Nothing saved yet."}</div>
                  </div>
                  {data.cultureThemes.filter(Boolean).length > 0 && (
                    <div className="mb-3 max-w-xs">
                      <label className={labelClass}>Filter by Status</label>
                      <select value={cultureStatusFilter} onChange={e => setCultureStatusFilter(e.target.value)} className={inputClass}>
                        <option value="">All Statuses</option>
                        <option value="Saved">Saved</option>
                      </select>
                    </div>
                  )}
                  <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                    <table className="w-full border-collapse">
                      <thead><tr>{["Culture Theme","Status"].map(h => <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>)}</tr></thead>
                      <tbody>
                        {!data.cultureThemes.filter(Boolean).length ? <tr><td colSpan={2} className="text-center py-4 text-[13px] text-[#617587]">No culture themes saved yet.</td></tr>
                          : (cultureStatusFilter && cultureStatusFilter !== "Saved" ? [] : data.cultureThemes.filter(Boolean)).length === 0 ? <tr><td colSpan={2} className="text-center py-4 text-[13px] text-[#617587]">No culture themes match the current filter.</td></tr>
                          : (cultureStatusFilter && cultureStatusFilter !== "Saved" ? [] : data.cultureThemes.filter(Boolean)).map((t, i) => (
                            <tr key={i} className="hover:bg-[#fcfdff]">
                              <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{t}</td>
                              <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><Tag cls="green">Saved</Tag></td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </Window>
              </div>
            )}

            {/* Ultimate Outcome Tab */}
            {activeTab === "outcome" && (
              <Window title="Ultimate Outcome" note="Capture what the school ultimately wants to create for students and the institution." headerRight={
                <>
                  <button onClick={() => setData(d => ({ ...d, ultimateOutcome: "", studentOutcome: "", institutionalOutcome: "" }))} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset</button>
                  <button onClick={handleSaveOutcome} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">Save Outcome</button>
                </>
              }>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="col-span-2"><label className={labelClass}>Ultimate Outcome Statement</label><textarea value={data.ultimateOutcome} onChange={e => setData(d => ({ ...d, ultimateOutcome: e.target.value }))} placeholder="Write the school's ultimate outcome here..." rows={3} className={textareaClass} /></div>
                  <div><label className={labelClass}>Student Outcome Focus</label><input value={data.studentOutcome} onChange={e => setData(d => ({ ...d, studentOutcome: e.target.value }))} placeholder="Example: Future-ready, ethical, resilient learners" className={inputClass} /></div>
                  <div><label className={labelClass}>Institutional Outcome Focus</label><input value={data.institutionalOutcome} onChange={e => setData(d => ({ ...d, institutionalOutcome: e.target.value }))} placeholder="Example: Trusted, high-performing, future-focused school" className={inputClass} /></div>
                </div>
                <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                  <table className="w-full border-collapse">
                    <thead><tr>{["Outcome Area","Saved Value"].map(h => <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>)}</tr></thead>
                    <tbody>
                      {!data.ultimateOutcome && !data.studentOutcome && !data.institutionalOutcome
                        ? <tr><td colSpan={2} className="text-center py-4 text-[13px] text-[#617587]">No ultimate outcome saved yet.</td></tr>
                        : [["Ultimate Outcome Statement", data.ultimateOutcome],["Student Outcome Focus", data.studentOutcome],["Institutional Outcome Focus", data.institutionalOutcome]].filter(([,v]) => v).map(([label, value], i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] font-semibold text-[#233445] border-b border-[#eef2f7]">{label}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#617587] border-b border-[#eef2f7]">{value}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Window>
            )}

            {/* Revision History Tab */}
            {activeTab === "history" && (
              <Window title="Revision History" note="Log important changes made in this module." headerRight={
                <>
                  <button onClick={() => { setHistoryForm({ date: "", by: "", change: "" }); setEditingHistoryIdx(null); }} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Input</button>
                  <button onClick={handleAddHistory} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">{editingHistoryIdx !== null ? "Update Entry" : "Add Entry"}</button>
                </>
              }>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div><label className={labelClass}>Revision Date</label><input type="date" value={historyForm.date} onChange={e => setHistoryForm(f => ({ ...f, date: e.target.value }))} className={inputClass} /></div>
                  <div><label className={labelClass}>Edited By</label><input value={historyForm.by} onChange={e => setHistoryForm(f => ({ ...f, by: e.target.value }))} placeholder="Enter person name..." className={inputClass} /></div>
                  <div className="col-span-2"><label className={labelClass}>What Changed</label><textarea value={historyForm.change} onChange={e => setHistoryForm(f => ({ ...f, change: e.target.value }))} placeholder="Describe what was updated..." rows={2} className={textareaClass} /></div>
                </div>
                <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                  <table className="w-full border-collapse">
                    <thead><tr>{["Date","Edited By","Change","Actions"].map(h => <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>)}</tr></thead>
                    <tbody>
                      {!data.revisionHistory.length ? <tr><td colSpan={4} className="text-center py-4 text-[13px] text-[#617587]">No revision entries yet.</td></tr>
                        : data.revisionHistory.map((h, i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{h.date}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{h.by}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#617587] border-b border-[#eef2f7]">{h.change}</td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><div className="flex gap-2">
                              <button onClick={() => { setHistoryForm(h); setEditingHistoryIdx(i); }} className="px-3 py-1 rounded-[8px] bg-[#eef6fc] text-[#2d81c1] text-[11px] font-semibold hover:bg-[#cfe3f3]">Edit</button>
                              <button onClick={() => { const rev = data.revisionHistory.filter((_,idx) => idx !== i); persist({ ...data, revisionHistory: rev }, "History entry deleted"); showToast("Entry deleted.", "warn"); }} className="px-3 py-1 rounded-[8px] bg-[#fff0f0] text-[#d95353] text-[11px] font-semibold hover:bg-[#f4c5c5]">Delete</button>
                            </div></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Window>
            )}

            {/* Preview Tab */}
            {activeTab === "preview" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Live Identity Preview" note="This is how current saved content is appearing right now.">
                  {[
                    { label: "Vision", value: data.vision || "Not added yet." },
                    { label: "Mission", value: data.mission || "Not added yet." },
                    { label: "Supporting Line", value: data.supportingLine || "Not added yet." },
                    { label: "Version Type", value: data.versionType || "Current Active Version" },
                  ].map((p, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[11px] font-semibold text-[#8ea0b1] uppercase tracking-wider mb-2">{p.label}</div>
                      <div className="text-[13px] text-[#233445]">{p.value}</div>
                    </div>
                  ))}
                </Window>

                <Window title="Connected Readiness" note="How ready this module is for later planning modules.">
                  {[
                    { title: "Identity completeness", text: completion >= 85 ? "This module is well-filled and ready for connected planning." : completion >= 55 ? "The module has good coverage but can be strengthened further." : "This module is still incomplete." },
                    { title: "Why it matters", text: "A clear identity makes diagnosis sharper, goals more relevant, and action plans easier to justify." },
                    { title: "Next connected page", text: "Once this is reasonably filled, move to School Information for the factual school baseline." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D3469]">Module Actions</h3>
                  <p className="text-sm text-gray-500">Save progress and manage sample/reset actions from one place.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={() => { persist(data, "Draft saved"); showToast("Draft saved.", "success"); }} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Save Draft
                  </button>
                  <button onClick={handleSaveIdentity} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Save Module
                  </button>
                  <button onClick={handleFillSample} className="border border-[#D67C2C] text-[#D67C2C] px-4 py-2 rounded-lg hover:bg-[#fff4eb] transition-colors font-medium">
                    Load Sample
                  </button>
                  <button onClick={handleClear} className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Clear Module
                  </button>
                  <Link to="/sdp/dashboard" className="bg-[#1D3469] hover:bg-[#16284f] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <ToastContainer />
    </>
  );
}
